import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, Copy, Check, Loader2, LogOut, Play, Share2 } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { useLang } from "@/lib/i18n";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { Chat } from "@/components/Chat";
import { inviteLink, shareInvite } from "@/lib/invite";

export default function Room() {
  const { id } = useParams();
  const { t } = useLang();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [room, setRoom] = useState(null);
  const [copied, setCopied] = useState(false);
  const [starting, setStarting] = useState(false);
  const navigatedRef = useRef(false);

  const fetchRoom = useCallback(async () => {
    try {
      const { data } = await api.get(`/rooms/${id}`);
      setRoom(data);
      if (data.status === "started" && data.match_id && !navigatedRef.current) {
        navigatedRef.current = true;
        navigate(`/match/${data.match_id}`);
      }
      if (data.status === "closed" && !navigatedRef.current) {
        navigatedRef.current = true;
        toast.error("Room closed");
        navigate("/lobby");
      }
    } catch (err) {
      if (!navigatedRef.current) {
        navigatedRef.current = true;
        navigate("/lobby");
      }
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchRoom();
    const iv = setInterval(fetchRoom, 1500);
    return () => clearInterval(iv);
  }, [fetchRoom]);

  const copyCode = () => {
    navigator.clipboard?.writeText(room.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const invite = async () => {
    const res = await shareInvite(room.code, t("invite_text"));
    if (res === "copied") toast.success(t("invite_copied"));
  };

  const start = async () => {
    setStarting(true);
    try {
      const { data } = await api.post(`/rooms/${id}/start`);
      navigatedRef.current = true;
      navigate(`/match/${data.match_id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setStarting(false);
    }
  };

  const leave = async () => {
    navigatedRef.current = true;
    try { await api.post(`/rooms/${id}/leave`); } catch (e) { console.warn("leave room failed:", e); }
    refreshUser();
    navigate("/lobby");
  };

  if (!room) {
    return <div className="min-h-screen bg-base flex items-center justify-center text-white/40 font-mono animate-pulse">…</div>;
  }

  return (
    <div className="min-h-screen bg-base text-white">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 md:px-8 py-14">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          className="border border-white/10 bg-panel p-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_0%,rgba(234,179,8,0.12),transparent_60%)]" />
          <div className="relative">
            <div className="flex items-center gap-2 text-signal">
              <Users className="w-5 h-5" />
              <span className="font-mono text-xs uppercase tracking-[0.3em]">{t("waiting_room")}</span>
            </div>

            <div className="mt-6">
              <div className="font-mono text-xs uppercase tracking-widest text-white/40">{t("share_code")}</div>
              <div className="mt-2 flex items-center gap-3">
                <div data-testid="room-code" className="font-mono font-black text-4xl tracking-[0.4em] text-signal">
                  {room.code}
                </div>
                <button
                  data-testid="copy-code-btn"
                  onClick={copyCode}
                  className="p-2 border border-white/15 rounded-sm hover:bg-white/5 transition-colors"
                >
                  {copied ? <Check className="w-4 h-4 text-profit" /> : <Copy className="w-4 h-4 text-white/60" />}
                </button>
              </div>
            </div>

            {room.status === "waiting" && (
              <div data-testid="invite-panel" className="mt-5 border border-signal/30 bg-signal/5 p-3 flex items-center gap-3 min-w-0">
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">{t("invite_friend")}</div>
                  <div data-testid="invite-link" className="font-mono text-xs text-white/70 truncate">{inviteLink(room.code)}</div>
                </div>
                <Button data-testid="invite-btn" onClick={invite} className="bg-signal text-black hover:bg-white rounded-sm font-semibold h-9 px-3 shrink-0">
                  <Share2 className="w-4 h-4" /> {t("invite_btn")}
                </Button>
              </div>
            )}

            <div className="mt-6 grid grid-cols-3 gap-px bg-white/10 border border-white/10">
              <div className="bg-panel p-3 text-center">
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">{t("buy_in")}</div>
                <div className="font-mono font-bold text-signal" data-testid="room-buyin">{room.buy_in === 0 ? t("free") : `€${room.buy_in}`}</div>
              </div>
              <div className="bg-panel p-3 text-center">
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">{t("pot")}</div>
                <div className="font-mono font-bold text-profit" data-testid="room-pot">€{room.pot.toFixed(2)}</div>
              </div>
              <div className="bg-panel p-3 text-center">
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">{t("capacity")}</div>
                <div className="font-mono font-bold text-white">{room.players.length}/{room.capacity}</div>
              </div>
            </div>

            <div className="mt-8">
              <div className="font-mono text-xs uppercase tracking-widest text-white/40 mb-3">
                {t("players_in_room")} · {room.players.length}
              </div>
              <div className="space-y-2">
                {room.players.map((p, i) => (
                  <div key={p.user_id} data-testid={`room-player-${i}`}
                    className="flex items-center gap-3 border border-white/10 bg-panelhover/40 px-4 py-3">
                    <span className="font-mono text-xs text-white/40 w-4">{i + 1}</span>
                    <span className="font-mono text-sm">{p.name}</span>
                    {p.user_id === room.host_id && (
                      <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-signal">host</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-8 flex items-center gap-3">
              {room.is_host ? (
                <Button
                  data-testid="start-match-btn"
                  onClick={start}
                  disabled={starting}
                  className="bg-signal text-black hover:bg-white rounded-sm font-semibold h-12 px-8 flex-1"
                >
                  {starting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Play className="w-5 h-5" />}
                  {t("start_match")}
                </Button>
              ) : (
                <div className="flex-1 flex items-center gap-2 text-white/50 font-mono text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" /> {t("waiting_host")}
                </div>
              )}
              <Button data-testid="leave-room-btn" onClick={leave} variant="outline"
                className="border-white/20 hover:bg-white/5 rounded-sm h-12">
                <LogOut className="w-4 h-4" /> {t("leave")}
              </Button>
            </div>
          </div>
        </motion.div>

        <div className="mt-6">
          <Chat channel={id} height={240} />
        </div>
      </div>
    </div>
  );
}
